import { createClient } from '@supabase/supabase-js';

export const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('Missing Supabase environment variables');
}

// Get the site URL from environment, fallback to window.location.origin
const siteUrl = import.meta.env.VITE_SITE_URL || window.location.origin;

// Create Supabase client with custom auth config and error handling
export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true,
    storage: window.localStorage,
    storageKey: 'supabase.auth.token',
    flowType: 'pkce',
    redirectTo: `${siteUrl}/auth/reset-password`,
    emailRedirectTo: `${siteUrl}/auth/reset-password`
  },
  global: {
    headers: {
      'X-Client-Info': 'sermonbuild-web',
      'X-Client-Version': '1.0.0'
    }
  },
  db: {
    schema: 'public'
  },
  realtime: {
    params: {
      eventsPerSecond: 1, // Reduced for better stability at scale
      heartbeatIntervalMs: 30000
    },
    reconnectAfterMs: (retries) => {
      // More conservative exponential backoff for production
      return Math.min(2000 + retries * 2000, 30000);
    }
  }
});

// Add health check function
export async function checkSupabaseConnection() {
  try {
    const { data: { session }, error: sessionError } = await supabase.auth.getSession();
    if (sessionError) throw sessionError;

    if (!session) {
      // No active session, try public endpoint
      const { error } = await supabase.from('subscription_tiers').select('id').limit(1);
      if (error) throw error;
    }

    return true;
  } catch (error) {
    console.error('Supabase connection error:', error);
    return false;
  }
}

// Add auth state change handler
supabase.auth.onAuthStateChange((event, session) => {
  if (event === 'SIGNED_OUT' || event === 'USER_DELETED') {
    if (!window.location.pathname.includes('/shared/') &&
        !window.location.pathname.includes('/series/')) {
      // Clear only auth-related keys — preserve user preferences (dark mode, font sizes, etc.)
      const AUTH_KEYS = ['isAdmin', 'isSuperAdmin', 'hasFreePremiun', 'pendingUpgrade',
                         'newGoogleSignup', 'welcomeModalShown', 'lastTrialReminderDate'];
      AUTH_KEYS.forEach(key => localStorage.removeItem(key));

      // Don't redirect if already on /auth — an OAuth callback may be in progress
      // and redirecting here would lose the auth code / interrupt the flow.
      if (!window.location.pathname.startsWith('/auth')) {
        window.location.href = '/auth';
      }
    }
  }
});